import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import GalleryHero from './components/GalleryHero';
import FilterTabs from './components/FilterTabs';
import GalleryGrid from './components/GalleryGrid';
import Lightbox from './components/Lightbox';
import GalleryCTA from './components/GalleryCTA';
import { galleryImages, GalleryCategory, featuredImages } from '../../mocks/galleryData';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://popprotents.com';

const galleryJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': `${siteUrl}/gallery/#gallery`,
  name: 'Stretch Tent Gallery — Weddings, Festivals & Events | Pop Pro Tents',
  description: 'Browse our gallery of stretch tent installations across Texas and nationwide. Weddings, music festivals, corporate events, private parties, and bespoke decor projects.',
  url: `${siteUrl}/gallery`,
  isPartOf: {
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    name: 'Pop Pro Tents',
    url: siteUrl,
  },
};

export default function GalleryPage() {
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState<GalleryCategory>(() => {
    const param = searchParams.get('category') as GalleryCategory | null;
    const valid: GalleryCategory[] = ['All', 'Weddings', 'Festivals', 'Corporate & Private Events', 'Bespoke Decor', 'Markets & Outdoor', 'Custom Skins'];
    return param && valid.includes(param) ? param : 'All';
  });
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    const param = searchParams.get('category') as GalleryCategory | null;
    const valid: GalleryCategory[] = ['All', 'Weddings', 'Festivals', 'Corporate & Private Events', 'Bespoke Decor', 'Markets & Outdoor', 'Custom Skins'];
    if (param && valid.includes(param)) {
      setActiveCategory(param);
    }
  }, [searchParams]);

  const filteredImages = useMemo(() => {
    const featured = featuredImages[activeCategory];
    if (activeCategory === 'All') {
      if (!featured) return galleryImages;
      const others = galleryImages.filter((img) => img.id !== featured.id);
      return [featured, ...others];
    }
    const categoryImages = galleryImages.filter((img) => img.category === activeCategory);
    if (!featured || !categoryImages.some((img) => img.id === featured.id)) {
      return categoryImages;
    }
    const others = categoryImages.filter((img) => img.id !== featured.id);
    return [featured, ...others];
  }, [activeCategory]);

  const handleOpenLightbox = (index: number) => setLightboxIndex(index);
  const handleCloseLightbox = () => setLightboxIndex(null);
  const handlePrev = () =>
    setLightboxIndex((i) => (i !== null ? (i - 1 + filteredImages.length) % filteredImages.length : 0));
  const handleNext = () =>
    setLightboxIndex((i) => (i !== null ? (i + 1) % filteredImages.length : 0));

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(galleryJsonLd) }}
      />
      <Navbar />
      <GalleryHero />
      <FilterTabs active={activeCategory} onChange={setActiveCategory} />
      <GalleryGrid
        images={filteredImages}
        activeCategory={activeCategory}
        onImageClick={handleOpenLightbox}
      />
      <GalleryCTA />
      <Footer />

      {lightboxIndex !== null && (
        <Lightbox
          images={filteredImages}
          currentIndex={lightboxIndex}
          onClose={handleCloseLightbox}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      )}
    </div>
  );
}